import React, { useEffect, useState } from "react";
import { Typography, Box, CircularProgress, Grid, Button, Modal, TextField, IconButton, Snackbar, Alert } from "@mui/material";
import axios from "axios";
import to from "await-to-js";
import { useParams } from "react-router-dom";
import { DataGrid, GridColDef, GridSortModel, GridFilterModel, GridRenderCellParams } from "@mui/x-data-grid";
import { axiosWithAuth } from "../plugins/axios";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";


const ListOfCohorts = () => {
  const { masterId } = useParams();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohortDetails, setCohortDetails] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const apiUrl = process.env.REACT_APP_PIPELINE_API;

  const [cloneOpen, setCloneOpen] = useState(false);
  const navigate = useNavigate();


  const [cloneFormData, setCloneFormData] = useState({
    cloneFromCohortId: cohortDetails.cohortId,
    cloneFromQuizId: cohortDetails.quizId,
    cloneToCohortId: "",
    cloneToQuizId: "",
    teachUrl: "",
  });

  const [cloneFormErrors, setCloneFormErrors] = useState({
    cloneToCohortId: false,
    cloneToQuizId: false,
    teachUrl: false,
  });

  const handleCloneOpen = () => {
    setCloneOpen(true);
    setCloneFormData({
        cloneFromCohortId: cohortDetails.cohortId,
        cloneFromQuizId: cohortDetails.quizId,
        cloneToCohortId: "",
        cloneToQuizId: "",
        teachUrl: "",
      });
  }
  const handleCloneClose = () => {
    setCloneOpen(false);
    setCloneFormErrors({
      cloneToCohortId: false,
      cloneToQuizId: false,
      teachUrl: false,
    });
  };
  const handleToastClose = () => setToastOpen(false);

  const handleCloneFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCloneFormData((prev) => ({ ...prev, [name as string]: value }));
    setCloneFormErrors((prev) => ({ ...prev, [name as string]: false }));
  };

  const handleCloneFormSubmit = async () => {
    const { cloneFromCohortId,cloneFromQuizId,cloneToCohortId, cloneToQuizId, teachUrl } = cloneFormData;

    // Validate required fields
    const errors = {
      cloneToCohortId: cloneToCohortId.trim() === "",
      cloneToQuizId: cloneToQuizId.trim() === "",
      teachUrl: teachUrl.trim() === "",
    };

    setCloneFormErrors(errors);

    if (Object.values(errors).some((error) => error)) return;
    setIsSubmitting(true);
    const requestData = {
        cloneFrom: {
          cohortId: cloneFromCohortId,
          quizId: cloneFromQuizId,
        },
        cloneTo: {
          cohortId: cloneToCohortId,
          quizId: cloneToQuizId,
        },
        teachUrl,
      };

      const [submitErr] = (await to(
        axiosWithAuth(
          `${apiUrl}cohortconfiguration/cloneSolution/create`, // Use the cloneSolution API endpoint
          requestData,
          "post"
        )
      )) as any;

      if (submitErr) {
        const { data: { message = null } = {} } = submitErr.response;
        setToastMessage(message);
        setToastOpen(true);
      }else {
        handleCloneClose(); 
        setCloneFormData({
            cloneFromCohortId: cohortDetails.cohortId,
            cloneFromQuizId: cohortDetails.quizId,
            cloneToCohortId: "",
            cloneToQuizId: "",
            teachUrl: "",
          });
          
        setCloneFormErrors({
            cloneToCohortId: false,
            cloneToQuizId: false,
            teachUrl: false,
        });
        setLoading(true);
  
        const [fetchErr, response] = await to(
          axiosWithAuth(
            `${apiUrl}cohortConfiguration/masterSolutions/cohortList/${masterId}`,
            {
              params: { page: page + 1, limit: rowsPerPage },
            },
            "get"
          )
        );
  
        if (!fetchErr) {
          setCohorts(response.data.data);
        }
      }
      setIsSubmitting(false); 
      setLoading(false)
    

  

    handleCloneClose();
  };


const fetchCohortDetails = async () => {
    const [err, response] = await to(
      axiosWithAuth(`${apiUrl}cohortConfiguration/cohort/masterSolutions/${masterId}`, {}, "get")
    );
  
    if (err) {
      setError("Failed to load cohort details");
      return;
    }
  
    setCohortDetails(response.data);
  };
  
  useEffect(() => {
    fetchCohortDetails();
  }, []);

  const fetchCohorts = async () => {
    
    const [err, response] = await to(
      axiosWithAuth(`${apiUrl}cohortConfiguration/masterSolutions/cohortList/${masterId}`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          sortColumn: sortModel[0]?.field || null,
          sortDirection: sortModel[0]?.sort || null,
          filterColumn: filterModel?.items[0]?.field || null,
          filterValue: filterModel?.items[0]?.value || null,
          filterOperator: filterModel?.items[0]?.operator || null,
        },
      }, "get")
    );

    if (err) {
      setError("Failed to load cohorts");
    } else {
      const { data, totalCount } = response.data;
    //   console.log(response.data)
      setCohorts(data);
      setTotalCount(totalCount);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCohorts();
  }, [page, rowsPerPage, sortModel, filterModel]);

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    { field: "cohortId", headerName: "Cohort ID", width: 150 ,
    renderCell: (params: GridRenderCellParams) => (
        <Button
          color="primary"
          onClick={() =>
            navigate(`/cohort/${params.row.cohortId}/quizzes`)
          }
        >
          {params.value}
        </Button>),},
    { field: "cohortName", headerName: "Cohort Name", width: 500 },
    { field: "quizId", headerName: "Quiz ID", width: 150 },
   
    { field: "clonedBy", headerName: "Cloned By", width: 300 }, 
    {
        field: "clonedOn",
        headerName: "Cloned On",
        width: 200,
        renderCell: (params: GridRenderCellParams) =>
          params.row?.clonedOn ? new Date(params.row?.clonedOn).toLocaleString() : "-",
        filterable: false,
      },
  ];

  return (

<Box mt={4}>
  <Box pb={4}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography gutterBottom>
          Cohort:{" "}
          <Typography display={"inline"} fontWeight={"bold"}>
            {cohortDetails.cohortId}
          </Typography>
        </Typography>
        <Typography fontWeight={"bold"}>{cohortDetails.cohortName}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography gutterBottom>
          Quiz:{" "}
          <Typography display={"inline"} fontWeight={"bold"}>
            {cohortDetails.quizId}
          </Typography>
        </Typography>
        <Typography gutterBottom fontWeight={"bold"}>
          {cohortDetails.quizName}
        </Typography>
      </Grid>
    </Grid>
  </Box>

  <Typography variant="h4" gutterBottom>
    List of Cohorts
  </Typography>

  {/* Aligning button with DataGrid */}
  <Box display="flex" justifyContent="flex-end" mb={2}>
  <Button
          variant="outlined"
          sx={{ minWidth: 180 }}
          onClick={handleCloneOpen}
        >
          Clone Solution
        </Button>

  </Box>

  <Box>
  <DataGrid
        rows={cohorts}
        columns={columns}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page: page, pageSize: rowsPerPage }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setRowsPerPage(model.pageSize);
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        disableRowSelectionOnClick
        loading={loading}
        getRowId={(row) => row.quizId}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        onFilterModelChange={(filter) => setFilterModel(filter)}
      />
  </Box>
  <Modal
        open={cloneOpen}
        onClose={handleCloneClose}
        aria-labelledby="clone-modal-title"
        aria-describedby="clone-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          {/* Modal Header */}
          <Typography
            id="clone-modal-title"
            variant="h6"
            display="flex"
            justifyContent="space-between"
          >
            Clone Autograder Solution
            <IconButton aria-label="close" onClick={handleCloneClose}>
              <CloseIcon />
            </IconButton>
          </Typography>

          {/* Clone From Section (Non-editable) */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Clone From
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Cohort ID"
              name="cloneFromCohortId"
              value={cloneFormData.cloneFromCohortId}
              disabled
              fullWidth
              margin="normal"
            />
            <TextField
              label="Quiz ID"
              name="cloneFromQuizId"
              value={cloneFormData.cloneFromQuizId}
              disabled
              fullWidth
              margin="normal"
            />
          </Box>

          {/* Clone To Section */}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Clone To
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Cohort ID"
              name="cloneToCohortId"
              value={cloneFormData.cloneToCohortId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneToCohortId}
              helperText={cloneFormErrors.cloneToCohortId && "Cohort ID is required"}
            />
            <TextField
              label="Quiz ID"
              name="cloneToQuizId"
              value={cloneFormData.cloneToQuizId}
              onChange={handleCloneFormChange}
              fullWidth
              margin="normal"
              error={cloneFormErrors.cloneToQuizId}
              helperText={cloneFormErrors.cloneToQuizId && "Quiz ID is required"}
            />
          </Box>

          {/* Teach URL Section */}
          <TextField
            label="Teach URL"
            name="teachUrl"
            value={cloneFormData.teachUrl}
            onChange={handleCloneFormChange}
            fullWidth
            margin="normal"
            error={cloneFormErrors.teachUrl}
            helperText={cloneFormErrors.teachUrl && "Teach URL is required"}
          />

          {/* Submit Button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCloneFormSubmit}
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? "Saving..." : "Submit"}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
</Box>

  

  );
};

export default ListOfCohorts;
